/**
 * useRealTimeUpdates Hook
 *
 * Generic hook for connecting to Firestore and subscribing to real-time events.
 * Reusable for any Firestore collection/query configuration.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { AgentProgressEvent } from '~/components/AiChat/AgentTimeline/types';
import type { IssueTokenResponse } from '~/components/AiChat/types';
import {
  type EventCursor,
  firestoreService,
  type FirestoreServiceInstance,
} from '~/services/firestore/firestoreService';

/** Floor for the local event map's cap; a wider `eventLimit` raises it so the query window is never trimmed. */
const MAX_EVENTS = 500;

export interface RealTimeEvent extends AgentProgressEvent {
  _isHistorical?: boolean;
}

export interface UseRealTimeUpdatesOptions {
  issueTokenResponse: IssueTokenResponse | null;
  enabled?: boolean;
  /**
   * Firestore service instance backing this subscription. Defaults to the shared chat
   * singleton; pass a dedicated instance (e.g. `notificationsFirestoreService`) for an
   * independent consumer so it doesn't disconnect or re-authenticate the chat connection.
   */
  service?: FirestoreServiceInstance;
  /** How many of the most recent events the Firestore query keeps; the service default when omitted. */
  eventLimit?: number;
  /**
   * Keep every event once it has loaded, and read earlier history on request. The live query is
   * bounded, so each new doc evicts the oldest as a `removed` change; `rtu_events` are append-only,
   * so that only means the doc left the window, and with this on it stays. Nothing is capped
   * either, or reading history in would evict it again. Off, the events are the window — what a
   * feed of the newest events wants.
   */
  keepHistory?: boolean;
}

export interface UseRealTimeUpdatesReturn {
  events: RealTimeEvent[];
  isConnected: boolean;
  hasReceivedInitialSnapshot: boolean;
  error: string | null;
  clearEvents: () => void;
  /** Whether history older than the loaded events exists. Only ever true with `keepHistory`. */
  hasEarlier: boolean;
  /** Reads the page of history before the loaded events into `events`. Rejects if the read fails. */
  loadEarlier: () => Promise<void>;
  /**
   * Reads every event from `since` (ISO-8601) up to the loaded ones into `events`, so what is
   * loaded stays one unbroken stretch. Rejects if the read fails.
   */
  loadSince: (since: string) => Promise<void>;
}

/** Adds the events the map does not hold yet, each created once. Returns whether any was new. */
const insertEvents = (
  eventMap: Map<string, RealTimeEvent>,
  events: AgentProgressEvent[],
  isHistorical: boolean
): boolean => {
  let didChange = false;
  for (const event of events) {
    if (eventMap.has(event.event_id)) continue;
    eventMap.set(event.event_id, { ...event, _isHistorical: isHistorical });
    didChange = true;
  }

  return didChange;
};

export function useRealTimeUpdates({
  issueTokenResponse = null,
  enabled = true,
  service = firestoreService,
  eventLimit,
  keepHistory = false,
}: UseRealTimeUpdatesOptions): UseRealTimeUpdatesReturn {
  const [events, setEvents] = useState<RealTimeEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [hasReceivedInitialSnapshot, setHasReceivedInitialSnapshot] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasEarlier, setHasEarlier] = useState(false);

  const initializedRef = useRef(false);
  const eventMapRef = useRef(new Map<string, RealTimeEvent>());
  /** Where the next read of earlier history starts; `null` while there is nothing before the events. */
  const earlierCursorRef = useRef<EventCursor | null>(null);
  /**
   * Bumped when a subscription ends or its history starts over, so a read made before that lands
   * afterwards is dropped.
   */
  const subscriptionRef = useRef(0);
  /** The reads of earlier history, chained so each starts from the cursor the one before it left. */
  const readsRef = useRef<Promise<void>>(Promise.resolve());

  const clearEvents = useCallback(() => {
    eventMapRef.current.clear();
    setEvents([]);
  }, []);

  const setEarlierCursor = useCallback((cursor: EventCursor | null) => {
    earlierCursorRef.current = cursor;
    setHasEarlier(cursor !== null);
  }, []);

  const readEarlier = useCallback(
    (since?: string): Promise<void> => {
      // Taken when the read is asked for, not when it starts: one queued behind another can start
      // after its subscription has gone, and must not spend the next one's cursor.
      const subscription = subscriptionRef.current;
      const isCurrent = () => subscription === subscriptionRef.current;

      const read = async (): Promise<void> => {
        const before = earlierCursorRef.current;
        if (!isCurrent() || !issueTokenResponse || !before) return;

        const earlier = await service.fetchEarlier(
          issueTokenResponse.firestore_config.collection_name,
          issueTokenResponse.query_config,
          { before, since }
        );
        if (!isCurrent()) return;

        setEarlierCursor(earlier.before);
        if (insertEvents(eventMapRef.current, earlier.events, true)) {
          setEvents(Array.from(eventMapRef.current.values()));
        }
      };

      const next = readsRef.current.then(read);
      // A failed read is its caller's to handle; the next one still runs.
      readsRef.current = next.catch(() => undefined);

      return next;
    },
    [issueTokenResponse, service, setEarlierCursor]
  );

  const loadEarlier = useCallback(() => readEarlier(), [readEarlier]);
  const loadSince = useCallback((since: string) => readEarlier(since), [readEarlier]);

  useEffect(() => {
    // Clear any stale error from a previous subscription before re-evaluating. Covers both the
    // disabled / token-cleared path (e.g. switching claims) and starting a fresh connection, so
    // the consumer reports loading or live data instead of a lingering error.
    setError(null);

    if (!enabled || !issueTokenResponse) {
      return;
    }

    const eventMap = eventMapRef.current;
    const maxEvents = Math.max(MAX_EVENTS, eventLimit ?? 0);
    eventMap.clear();
    setEvents([]);
    setHasReceivedInitialSnapshot(false);

    if (initializedRef.current) {
      service.disconnect();
      initializedRef.current = false;
    }

    let aborted = false;

    const initializeConnection = async () => {
      try {
        await service.initialize(
          issueTokenResponse.firestore_config,
          issueTokenResponse.firebase_web_app_config,
          issueTokenResponse.firebase_custom_token
        );

        // Effect cleanup ran while awaiting initialize — tear down and bail to avoid an orphaned listener
        if (aborted) {
          service.disconnect();
          return;
        }

        setIsConnected(true);
        initializedRef.current = true;

        service.subscribe(
          issueTokenResponse.firestore_config.collection_name,
          issueTokenResponse.query_config,
          (incomingEvents: AgentProgressEvent[], isHistorical: boolean, removedEventIds: string[]) => {
            let didChange = insertEvents(eventMap, incomingEvents, isHistorical);

            // Prune docs the bounded Firestore query evicted as newer events arrived. Without this the
            // local map keeps growing (up to maxEvents) past the query window, undermining the bound
            // and showing stale items in feeds like STP notifications.
            if (!keepHistory) {
              for (const removedId of removedEventIds) {
                if (eventMap.delete(removedId)) didChange = true;
              }
            }

            // Nothing was added or removed (e.g. an all-duplicate snapshot) — skip the state update
            // entirely so a redundant snapshot can't trigger a re-render.
            if (!didChange) return;

            if (!keepHistory && eventMap.size > maxEvents) {
              // Drop the oldest events *by timestamp*, not by insertion order: a descending initial
              // snapshot inserts newest-first, so trimming by insertion order would evict the newest
              // events — the opposite of intent. ISO 8601 timestamps sort lexicographically.
              const evictCount = eventMap.size - maxEvents;
              const idsToDelete = Array.from(eventMap.values())
                .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
                .slice(0, evictCount)
                .map((event) => event.event_id);
              for (const id of idsToDelete) {
                eventMap.delete(id);
              }
            }

            // One commit (and one array rebuild) per snapshot, regardless of how many events it
            // carried — keeps render work ~O(snapshots) instead of O(events) during a long run.
            setEvents(Array.from(eventMap.values()));
          },
          (err: Error) => {
            setError(err.message);
            setIsConnected(false);
          },
          (earlier: EventCursor | null, windowEventIds: string[]) => {
            if (keepHistory) {
              // The history starts over from this window. Drop what no longer joins up with it, so what
              // is loaded stays one unbroken stretch and reading earlier fills the gap instead of
              // starting below it; a read still in flight would land below it too.
              const inWindow = new Set(windowEventIds);
              let didPrune = false;
              for (const id of Array.from(eventMap.keys())) {
                if (!inWindow.has(id) && eventMap.delete(id)) didPrune = true;
              }
              if (didPrune) setEvents(Array.from(eventMap.values()));
              subscriptionRef.current += 1;
              readsRef.current = Promise.resolve();
              setEarlierCursor(earlier);
            }
            // The first snapshot has landed (possibly empty) — initial data is now in `events`, so a
            // consumer's loading state can end without flashing an empty state beforehand.
            setHasReceivedInitialSnapshot(true);
          },
          eventLimit
        );
      } catch (err) {
        if (aborted) return;
        setError(err instanceof Error ? err.message : 'Unknown error');
        setIsConnected(false);
        initializedRef.current = false;
      }
    };

    initializeConnection();

    return () => {
      aborted = true;
      eventMap.clear();
      setEvents([]);
      if (initializedRef.current) {
        service.disconnect();
        initializedRef.current = false;
      }
      setIsConnected(false);
      setHasReceivedInitialSnapshot(false);
      // The history belongs to this subscription: drop its cursor, and whatever it still has in flight.
      subscriptionRef.current += 1;
      readsRef.current = Promise.resolve();
      setEarlierCursor(null);
    };
  }, [enabled, issueTokenResponse, service, eventLimit, keepHistory, setEarlierCursor]);

  return {
    events,
    isConnected,
    hasReceivedInitialSnapshot,
    error,
    clearEvents,
    hasEarlier,
    loadEarlier,
    loadSince,
  };
}
